document.addEventListener('DOMContentLoaded', () => {
  // Global variables for PDF handling
  let pdfDoc = null;
  let currentPage = 1;
  let totalPages = 0;
  let scale = 1; // Default scale for zoom

  /**
   * Initialize file preview functionality for all file preview items
   */
  const initializeFilePreview = () => {
    const items = document.querySelectorAll('.file-preview');

    if (items.length > 0) {
      items.forEach((item) => {
        setupFilePreviewItem(item);
      });
    }
  };

  /**
   * Setup individual file preview item
   * @param {HTMLElement} item - The file preview item element
   */
  const setupFilePreviewItem = (item) => {
    // Get unique IDs from data attributes (new way) or use default IDs (old way)
    let modalId = item.getAttribute('data-modal-id');
    let contentId = item.getAttribute('data-content-id');

    // Fallback to default IDs for backward compatibility
    if (!modalId || !contentId) {
      modalId = 'filePreviewModal';
      contentId = 'filePreviewContent';
    }

    const modal = document.getElementById(modalId);
    const modalContent = document.getElementById(contentId);

    if (!modal || !modalContent) {
      console.warn('Modal or modal content not found for IDs:', modalId, contentId);
      return;
    }

    // Setup download links to prevent modal opening
    const downloadLinks = item.querySelectorAll('.js-file-download');
    if (downloadLinks.length > 0) {
      downloadLinks.forEach((downloadLink) => {
        downloadLink.addEventListener('click', (event) => {
          event.stopPropagation();
        });
      });
    }

    // Setup main click handler for opening modal
    item.addEventListener('click', (event) => {
      if (event.target.closest('.js-file-download')) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      let fileUrl = item.getAttribute('data-file-url') || '';

      // Fix protocol for non-local URLs
      if (fileUrl.startsWith('http://')) {
        if (!fileUrl.includes('qaro.local')) {
          fileUrl = fileUrl.replace('http://', 'https://');
        }
      }

      const extension = fileUrl ? getFileExtension(fileUrl) : '';

      if (!fileUrl) {
        modalContent.innerHTML = '<p>Soubor není dostupný.</p>';
        return;
      }

      // Show spinner while loading
      const spinner = document.createElement('div');
      spinner.classList.add('spinner');
      modalContent.innerHTML = '';
      modalContent.appendChild(spinner);

      // Open modal
      modal.classList.add('is-open');
      document.body.classList.add('is-unscrollable');

      // Load content after delay for UX
      setTimeout(() => {
        modalContent.innerHTML = getPreviewContent(fileUrl, extension, contentId);
        if (extension === 'pdf') {
          loadPdfJs(fileUrl, contentId);
        } else if (['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'odt', 'ods', 'odp'].includes(extension)) {
          setupDownloadButton(contentId);
        }
      }, 300);
    });

    // Setup close buttons
    setupModalCloseHandlers(modal);
  };

  /**
   * Setup modal close handlers
   * @param {HTMLElement} modal - The modal element
   */
  const setupModalCloseHandlers = (modal) => {
    const closeButtons = modal.querySelectorAll('.js-modal-close');
    if (closeButtons.length > 0) {
      closeButtons.forEach((button) => {
        button.addEventListener('click', (event) => {
          event.preventDefault();
          closeModal(modal);
        });
      });
    }

    // Close on background click
    modal.addEventListener('click', (event) => {
      if (event.target === modal) {
        closeModal(modal);
      }
    });
  };

  /**
   * Close modal and cleanup
   * @param {HTMLElement} modal - The modal element to close
   */
  const closeModal = (modal) => {
    modal.classList.remove('is-open');
    document.body.classList.remove('is-unscrollable');
  };

  /**
   * Get file extension from URL
   * @param {string} url - File URL
   * @returns {string} File extension in lowercase
   */
  const getFileExtension = (url) => {
    const parts = url.split('.');
    return parts[parts.length - 1].toLowerCase();
  };

  /**
   * Universal download function for all file types
   * @param {string} contentId - The content container ID for scoping
   */
  const setupDownloadButton = (contentId) => {
    setTimeout(() => {
      const contentContainer = document.getElementById(contentId);
      if (!contentContainer) return;

      const downloadButton = contentContainer.querySelector('#downloadPdf');
      if (!downloadButton) {
        console.warn('Download button not found in container:', contentId);
        return;
      }

      // Remove existing event listeners to prevent duplicates
      downloadButton.replaceWith(downloadButton.cloneNode(true));
      const newDownloadButton = contentContainer.querySelector('#downloadPdf');

      newDownloadButton.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();

        const url = newDownloadButton.getAttribute('data-url');
        if (!url) {
          console.error('No URL found for download');
          return;
        }

        const a = document.createElement('a');
        a.href = url;
        a.download = '';
        a.target = '_blank';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      });
    }, 100);
  };

  /**
   * Generate preview content HTML based on file type
   * @param {string} url - File URL
   * @param {string} extension - File extension
   * @param {string} contentId - Content container ID for scoping
   * @returns {string} HTML content for preview
   */
  const getPreviewContent = (url, extension, contentId) => {
    if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'heic', 'heif', 'avif'].includes(extension)) {
      return `<img src="${url}" alt="File Preview" style="max-width: 100%; max-height: 80vh;">`;
    } else if (extension === 'pdf') {
      return `
        <div class="pdfControls pdfControls__top">
          <button id="zoomOut_${contentId}" class="btn btn--compact">
            <span class="icon icon--icon-zoom-out">
              <svg class="icon__svg" viewBox="0 0 24 24" width="24" height="24" xmlns="http://www.w3.org/2000/svg" fill-rule="evenodd" clip-rule="evenodd"><path d="M15.853 16.56c-1.683 1.517-3.911 2.44-6.353 2.44-5.243 0-9.5-4.257-9.5-9.5s4.257-9.5 9.5-9.5 9.5 4.257 9.5 9.5c0 2.442-.923 4.67-2.44 6.353l7.44 7.44-.707.707-7.44-7.44zm-6.353-15.56c4.691 0 8.5 3.809 8.5 8.5s-3.809 8.5-8.5 8.5-8.5-3.809-8.5-8.5 3.809-8.5 8.5-8.5zm-4.5 8h9v1h-9v-1z"/></svg>
            </span>
          </button>
          <button id="zoomIn_${contentId}" class="btn btn--compact">
            <span class="icon icon--icon-zoom-in">
              <svg class="icon__svg" viewBox="0 0 24 24" width="24" height="24" xmlns="http://www.w3.org/2000/svg" fill-rule="evenodd" clip-rule="evenodd"><path d="M15.853 16.56c-1.683 1.517-3.911 2.44-6.353 2.44-5.243 0-9.5-4.257-9.5-9.5s4.257-9.5 9.5-9.5 9.5 4.257 9.5 9.5c0 2.442-.923 4.67-2.44 6.353l7.44 7.44-.707.707-7.44-7.44zm-6.353-15.56c4.691 0 8.5 3.809 8.5 8.5s-3.809 8.5-8.5 8.5-8.5-3.809-8.5-8.5 3.809-8.5 8.5-8.5zm-4.5 8h4v-4h1v4h4v1h-4v4h-1v-4h-4v-1z"/></svg>
            </span>
          </button>
          <button id="downloadPdf" class="btn btn--compact" data-url="${url}">
            <span class="icon icon--icon-download">
              <svg class="icon__svg" viewBox="0 0 24 24" width="24" height="24" xmlns="http://www.w3.org/2000/svg" fill-rule="evenodd" clip-rule="evenodd"><path d="M12 21l-8-9h5v-12h6v12h5l-8 9zm9-1v2h-18v-2h18z"/>
            </span>
          </button>
        </div>
        <div id="pdfViewer_${contentId}" style="width: 100%; height: 80vh;"></div>
        <div class="pdfControls pdfControls__bottom">
          <button id="prevPage_${contentId}" class="btn btn--compact">
            <span class="icon icon--icon-chevron-left">
              <svg class="icon__svg"><use xlink:href="#icon-chevron-left"></use></svg>
            </span>
          </button>
          <span id="pageInfo_${contentId}" class="pageInfo">Strana <span id="currentPage_${contentId}">1</span> z <span id="totalPages_${contentId}">?</span></span>
          <button id="nextPage_${contentId}" class="btn btn--compact">
            <span class="icon icon--icon-chevron-right">
              <svg class="icon__svg"><use xlink:href="#icon-chevron-right"></use></svg>
            </span>
          </button>
        </div>
      `;
    } else if (['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'odt', 'ods', 'odp'].includes(extension)) {
      const officeUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(url)}`;
      return `
        <div class="pdfControls pdfControls__top">
          <button id="downloadPdf" class="btn btn--compact" data-url="${url}">
            <span class="icon icon--icon-download">
              <svg class="icon__svg" viewBox="0 0 24 24" width="24" height="24" xmlns="http://www.w3.org/2000/svg" fill-rule="evenodd" clip-rule="evenodd"><path d="M12 21l-8-9h5v-12h6v12h5l-8 9zm9-1v2h-18v-2h18z"/>
            </span>
          </button>
        </div>
        <iframe src="${officeUrl}" style="width: 100%; height: 80vh;" frameborder="0" sandbox="allow-same-origin allow-forms allow-scripts allow-popups"></iframe>
      `;
    } else {
      return `<p>Preview není dostupný pro tento typ souboru. <a href="${url}" target="_blank">Stáhněte si soubor</a>.</p>`;
    }
  };

  /**
   * Load PDF.js library and display PDF
   * @param {string} url - PDF file URL
   * @param {string} contentId - Content container ID for scoping
   */
  const loadPdfJs = (url, contentId) => {
    currentPage = 1;
    setupDownloadButton(contentId);

    const script = document.createElement('script');
    script.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.3.122/pdf.min.js';
    script.onload = () => {
      pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.3.122/pdf.worker.min.js';

      pdfjsLib.getDocument(url).promise.then(function (pdf) {
        pdfDoc = pdf;
        totalPages = pdf.numPages;

        const totalPagesElement = document.getElementById(`totalPages_${contentId}`);
        if (totalPagesElement) {
          totalPagesElement.textContent = totalPages;
        }

        renderPage(currentPage, contentId);
        setupPdfControls(contentId);
      });
    };

    script.onerror = () => {
      console.error('Failed to load PDF.js');
    };

    document.body.appendChild(script);
  };

  /**
   * Setup PDF control buttons
   * @param {string} contentId - Content container ID for scoping
   */
  const setupPdfControls = (contentId) => {
    const prevButton = document.getElementById(`prevPage_${contentId}`);
    const nextButton = document.getElementById(`nextPage_${contentId}`);
    const zoomInButton = document.getElementById(`zoomIn_${contentId}`);
    const zoomOutButton = document.getElementById(`zoomOut_${contentId}`);

    if (prevButton) prevButton.addEventListener('click', () => onPrevPage(contentId));
    if (nextButton) nextButton.addEventListener('click', () => onNextPage(contentId));
    if (zoomInButton) zoomInButton.addEventListener('click', () => onZoomIn(contentId));
    if (zoomOutButton) zoomOutButton.addEventListener('click', () => onZoomOut(contentId));
  };

  /**
   * Render PDF page
   * @param {number} pageNum - Page number to render
   * @param {string} contentId - Content container ID for scoping
   */
  const renderPage = (pageNum, contentId) => {
    pdfDoc.getPage(pageNum).then(function (page) {
      const viewport = page.getViewport({ scale });
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');

      canvas.height = viewport.height;
      canvas.width = viewport.width;

      const pdfViewer = document.getElementById(`pdfViewer_${contentId}`);
      if (pdfViewer) {
        pdfViewer.innerHTML = '';
        pdfViewer.appendChild(canvas);
      }

      const renderContext = {
        canvasContext: context,
        viewport: viewport,
      };

      page.render(renderContext).promise.then(function () {
        const currentPageElement = document.getElementById(`currentPage_${contentId}`);
        if (currentPageElement) {
          currentPageElement.textContent = pageNum;
        }
      });
    });
  };

  /**
   * Navigate to previous page
   * @param {string} contentId - Content container ID for scoping
   */
  const onPrevPage = (contentId) => {
    if (currentPage <= 1) return;
    currentPage--;
    renderPage(currentPage, contentId);
  };

  /**
   * Navigate to next page
   * @param {string} contentId - Content container ID for scoping
   */
  const onNextPage = (contentId) => {
    if (currentPage >= totalPages) return;
    currentPage++;
    renderPage(currentPage, contentId);
  };

  /**
   * Zoom in PDF
   * @param {string} contentId - Content container ID for scoping
   */
  const onZoomIn = (contentId) => {
    scale += 0.25;
    renderPage(currentPage, contentId);
  };

  /**
   * Zoom out PDF
   * @param {string} contentId - Content container ID for scoping
   */
  const onZoomOut = (contentId) => {
    if (scale > 0.5) {
      scale -= 0.25;
      renderPage(currentPage, contentId);
    }
  };

  // Initialize the file preview functionality
  initializeFilePreview();
});
